import {
    Component,
    Input,
    OnDestroy,
    OnInit,
    ChangeDetectionStrategy,
} from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { NestedFormGroupType } from '../../../../../../../core/models/form-group-type';
import { AttachmentFormModel } from '../../../models/attachment/attachment-form-model';
import { AttachmentOperationFormModel } from '../../../models/attachment/attachment-operation-form-model';
import { AttachmentFormService } from '../attachment-operation/services/attachment-form/attachment-form.service';
import { Subject, takeUntil, tap } from 'rxjs';
import { AttachmentResponse } from '../../../models/attachment/attachment-response';
import { AttachmentService } from '../../../services/attachment/attachment.service';
import { AttachmentItemComponent } from '../attachment-item/attachment-item.component';
import { AttachmentOperationFormService } from '../attachment-operation/services/attachment-operation-form/attachment-operation-form.service';

@Component({
    selector: 'app-attachment-list',
    templateUrl: './attachment-list.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [AttachmentItemComponent],
})
export class AttachmentListComponent implements OnInit, OnDestroy {
    @Input() applicationId!: string;
    @Input() form!: FormGroup<
        NestedFormGroupType<AttachmentOperationFormModel>
    >;

    private destroy$ = new Subject<void>();

    constructor(
        private readonly attachmentService: AttachmentService,
        private readonly attachmentFormService: AttachmentFormService,
        private readonly attachmentOperationFormService: AttachmentOperationFormService,
    ) {}

    ngOnInit(): void {
        this.loadAttachments();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    get attachments(): FormArray<
        FormGroup<NestedFormGroupType<AttachmentFormModel>>
    > {
        return this.attachmentOperationFormService.getAttachments(this.form);
    }

    /**
     * Checks if there are any attachments that are not marked for deletion.
     *
     * @return true if there is at least one attachment not marked for deletion, false otherwise.
     */
    hasActiveAttachments(): boolean {
        return this.attachments.controls.some(
            (
                attachmentForm: FormGroup<
                    NestedFormGroupType<AttachmentFormModel>
                >,
            ) => {
                return attachmentForm.value.isDeleted === false; // If there is at least one attachment not marked for deletion, return true
            },
        );
    }

    /**
     * Removes the attachment from the form array. If the attachment is new (not yet saved to the backend),
     * it is removed from the form array. If the attachment already exists in the backend, it is marked
     * for deletion by setting the isDeleted flag to true.
     *
     * @param guid the guid of the attachment to be removed.
     */
    removeAttachment(guid: string): void {
        this.attachments.controls.forEach(
            (
                attachmentForm: FormGroup<
                    NestedFormGroupType<AttachmentFormModel>
                >,
            ) => {
                if (attachmentForm.value.guid === guid) {
                    if (
                        this.attachmentFormService.isNewAttachment(
                            attachmentForm,
                        )
                    ) {
                        const index =
                            this.attachments.controls.indexOf(attachmentForm);
                        this.attachments.removeAt(index);
                        return;
                    }

                    if (
                        this.attachmentFormService.isExistingAttachment(
                            attachmentForm,
                        )
                    ) {
                        (
                            attachmentForm.controls.isDeleted as FormControl
                        ).patchValue(true);
                        return;
                    }
                }
            },
        );
    }

    /**
     * Loads all attachments for the given application ID and adds them to the form array.
     */
    private loadAttachments(): void {
        this.attachmentService
            .getAll(this.applicationId)
            .pipe(
                takeUntil(this.destroy$),
                tap((attachments: AttachmentResponse[]) => {
                    attachments.forEach((attachment: AttachmentResponse) => {
                        this.addAttachment(attachment);
                    });
                }),
            )
            .subscribe();
    }

    /**
     * Adds a new attachment form to the form array. The form is then marked as touched to trigger validation.
     *
     * @param data Partial data to pre-fill the attachment form.
     */
    private addAttachment(data: Partial<AttachmentFormModel>): void {
        const attachmentForm: FormGroup<
            NestedFormGroupType<AttachmentFormModel>
        > = this.attachmentFormService.getFormDefinition();
        attachmentForm.patchValue(data);

        this.attachmentOperationFormService.addAttachment(
            this.form,
            attachmentForm,
        );
    }
}
